import { useState, useEffect } from 'react';
import { getPaymentsHistory, type Payment } from '../../api/payments.api';
import { formatCOP } from '../../utils/currencyFormat';

export default function PagosPage() {
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const data = await getPaymentsHistory();
            setPayments(data);
        } catch (error) {
            console.error('Error loading payments:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="p-4 sm:p-6">
                <div className="mb-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Historial de Pagos</h1>
                    <p className="text-sm text-slate-500">Búsqueda y auditoría de transacciones.</p>
                </div>

                {/* Vista de tabla - Desktop */}
                <div className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Fecha</th>
                                    <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Boleto / Placa</th>
                                    <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Método</th>
                                    <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Monto</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                                            <i className="ri-loader-4-line animate-spin text-2xl"></i>
                                        </td>
                                    </tr>
                                ) : payments.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                                            No hay pagos registrados.
                                        </td>
                                    </tr>
                                ) : (
                                    payments.map((p) => (
                                        <tr key={p.id} className="hover:bg-slate-50/50">
                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                {new Date(p.createdAt).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-800">{p.boleto || 'N/A'}</div>
                                                <div className="text-xs text-slate-500">{p.vehiculo?.placa || 'N/A'}</div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="px-2 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                                                    {p.metodoPago}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-bold text-right text-green-600">
                                                {formatCOP(p.total)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Vista de tarjetas - Móvil */}
                <div className="md:hidden space-y-3">
                    {loading ? (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
                            <i className="ri-loader-4-line animate-spin text-2xl text-slate-500"></i>
                        </div>
                    ) : payments.length === 0 ? (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-500">
                            No hay pagos registrados.
                        </div>
                    ) : (
                        payments.map((p) => (
                            <div key={p.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                        <div className="font-bold text-slate-800 text-base mb-1">
                                            {p.boleto || 'N/A'}
                                        </div>
                                        <div className="text-sm text-slate-500 flex items-center gap-1">
                                            <i className="ri-car-line"></i>
                                            {p.vehiculo?.placa || 'N/A'}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-green-600">
                                            {formatCOP(p.total)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                        <i className="ri-time-line"></i>
                                        {new Date(p.createdAt).toLocaleString('es-CO', {
                                            day: '2-digit',
                                            month: 'short',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </div>
                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                                        {p.metodoPago}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
}
